import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { AutoCompleteItem } from "@/components/AutoComplete/AutoComplete";
import { useGetLemlistsQuery } from "@/pages/Private/redux/lemlist/lemlist.api";
import { useGetListQuery } from "@/pages/Private/redux/list/list.api";
import { useGetWebhooksQuery } from "@/pages/Private/redux/webhook/webhook.api";
import { useSearch } from "@/utils";

import {
	getBlockListFrequencyInfoKey,
	getDropdownDataIds,
	shouldBlockListFrequency,
} from "../utils/listSettings.utils";

export const useListSettingsData = (listId: number) => {
	const { t } = useTranslation();
	const tsSettings = (key: string) => t(`list.settings.${key}`);

	// Search for lemlists
	const { query: lemlistQuery, setSearchValue: setSearchLemlist } = useSearch();

	// Fetch list data
	const { data, isFetching } = useGetListQuery(listId);

	// Fetch lemlists for campaign selection
	const { data: lemlists, isFetching: isLoadingLemlists } = useGetLemlistsQuery({
		page: 1,
		limit: 10,
		searchValue: lemlistQuery,
	});

	// Fetch webhooks
	const { data: webhooks } = useGetWebhooksQuery({
		page: 1,
		limit: 100,
	});

	// Computed values
	const blockListFrequency = useMemo(() => shouldBlockListFrequency(data), [data]);

	const blockListFrequencyInfoKey = useMemo(
		() => getBlockListFrequencyInfoKey(data, blockListFrequency),
		[data, blockListFrequency]
	);

	const blockListFrequencyInfo = useMemo(
		() => (blockListFrequencyInfoKey ? tsSettings(blockListFrequencyInfoKey) : undefined),
		[blockListFrequencyInfoKey, tsSettings]
	);

	// Build dropdown data with translations
	const dropdownData = useMemo(() => {
		const ids = getDropdownDataIds(data);
		const viewIdToTitleKey: Record<string, string> = {
			leadList: "viewLeadList",
			listSources: "viewSources",
			findPerson: "viewFindPerson",
			leadQualification: "viewCompanyQualification",
			copywriting: "viewCampaigns",
			settings: "viewListSettings",
			signals: "viewSignals",
		};

		return ids.map((id) => ({
			title: tsSettings(viewIdToTitleKey[id] || id),
			id,
		})) as unknown as AutoCompleteItem[];
	}, [data, tsSettings]);

	return {
		// List data
		data,
		isFetching,

		// Lemlists
		lemlists,
		isLoadingLemlists,
		lemlistQuery,
		setSearchLemlist,

		// Webhooks
		webhooks,

		// Computed values
		blockListFrequency,
		blockListFrequencyInfo,
		dropdownData,
	};
};
